import {CdkMenu, CdkMenuItem, CdkMenuTrigger} from "@angular/cdk/menu"
import {ChangeDetectionStrategy, Component, computed, input, output, signal} from "@angular/core"
import {AssetActionsMenuComponent, AssetCellChipComponent, AssetGridCellComponent, AssetMenuAction} from "@common/components/asset-grid"
import {LoadingSpinnerIconComponent} from "@common/components/progress/loading-spinner-icon/loading-spinner-icon.component"
import {JobState, MaterialOutputType} from "@generated"
import {PbrExportOptionLabels} from "@labels"
import type {MaterialAssetGridOutputFragment} from "@platform/components/materials/material-asset-grid/material-asset-grid/material-asset-grid.generated"

type PbrResult = NonNullable<MaterialAssetGridOutputFragment["result"]>

type PbrCellState =
    | {kind: "empty"; selectedType: MaterialOutputType}
    | {kind: "loading"; selectedType: MaterialOutputType}
    | {kind: "failed"; selectedType: MaterialOutputType}
    | {kind: "complete"; selectedType: MaterialOutputType; result: PbrResult}

type OptionEntry = {
    type: MaterialOutputType
    label: string
    isSelected: boolean
    isRendered: boolean
    isLoading: boolean
    result: PbrResult | null
}

// Fixed presentation order. Drives both the dropdown order and the "first available" selection.
const PBR_TYPE_ORDER: readonly MaterialOutputType[] = [
    MaterialOutputType.PbrExportDefaultJpegLow,
    MaterialOutputType.PbrExportDefaultJpegOriginal,
    MaterialOutputType.PbrExportDefaultTiffLow,
    MaterialOutputType.PbrExportDefaultTiffOriginal,
    MaterialOutputType.PbrExportDefaultExrOriginal,
]

const DEFAULT_TYPE = MaterialOutputType.PbrExportDefaultTiffOriginal

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-pbr-asset-cell",
    templateUrl: "./pbr-asset-cell.component.html",
    styleUrls: ["./pbr-asset-cell.component.scss"],
    imports: [AssetActionsMenuComponent, AssetCellChipComponent, AssetGridCellComponent, CdkMenu, CdkMenuItem, CdkMenuTrigger, LoadingSpinnerIconComponent],
})
export class PbrAssetCellComponent {
    readonly $outputs = input<readonly MaterialAssetGridOutputFragment[]>([], {alias: "outputs"})
    readonly $pendingTypes = input<ReadonlySet<MaterialOutputType>>(new Set(), {alias: "pendingTypes"})
    readonly $hasCyclesMaterial = input<boolean>(false, {alias: "hasCyclesMaterial"})
    readonly $canEdit = input<boolean>(false, {alias: "canEdit"})
    readonly $canDownload = input<boolean>(false, {alias: "canDownload"})

    readonly render = output<MaterialOutputType>()
    readonly rerender = output<MaterialOutputType>()
    readonly remove = output<MaterialOutputType>()
    readonly cancel = output<MaterialOutputType>()
    readonly retry = output<MaterialOutputType>()

    private readonly $renderedOutputsByType = computed(() => {
        const map = new Map<MaterialOutputType, MaterialAssetGridOutputFragment>()
        for (const output of this.$outputs()) {
            if (output.result) map.set(output.type, output)
        }
        return map
    })

    private readonly $userSelectedType = signal<MaterialOutputType | null>(null)

    readonly $selectedType = computed<MaterialOutputType>(() => {
        const user = this.$userSelectedType()
        if (user) return user
        const rendered = this.$renderedOutputsByType()
        const first = PBR_TYPE_ORDER.find((type) => rendered.has(type))
        return first ?? DEFAULT_TYPE
    })

    // A material can carry several outputs of the same type (one per revision).
    // Pick a single canonical output per type (last wins) and use it everywhere so
    // the top-right chip and the dropdown row never disagree on an option's state.
    private readonly $outputByType = computed(() => new Map(this.$outputs().map((o) => [o.type, o])))

    readonly $selectedOutput = computed(() => this.$outputByType().get(this.$selectedType()))
    readonly $selectedPending = computed(() => this.$pendingTypes().has(this.$selectedType()))

    readonly $state = computed<PbrCellState>(() => {
        const selectedType = this.$selectedType()
        const output = this.$selectedOutput()
        const jobState = output?.job?.state
        if (this.$selectedPending() || (jobState && isJobRunning(jobState))) return {kind: "loading", selectedType}
        if (!output) return {kind: "empty", selectedType}
        if (jobState === JobState.Failed || jobState === JobState.Cancelled) return {kind: "failed", selectedType}
        if (output.result) return {kind: "complete", selectedType, result: output.result}
        return {kind: "empty", selectedType}
    })

    readonly $options = computed<readonly OptionEntry[]>(() => {
        const selectedType = this.$selectedType()
        const pendingTypes = this.$pendingTypes()
        const outputsByType = this.$outputByType()
        return PBR_TYPE_ORDER.map((type) => {
            const output = outputsByType.get(type)
            const result = output?.result ?? null
            const jobState = output?.job?.state
            return {
                type,
                label: PbrExportOptionLabels.get(type)?.label ?? "",
                isSelected: type === selectedType,
                isRendered: !!result,
                isLoading: pendingTypes.has(type) || (jobState ? isJobRunning(jobState) : false),
                result,
            }
        })
    })

    private readonly $selectedState = computed(() => PbrExportOptionLabels.get(this.$selectedType())?.state)
    readonly $selectedFiletype = computed(() => this.$selectedState()?.format.toUpperCase() ?? "")
    readonly $selectedResolution = computed(() => {
        const state = this.$selectedState()
        if (!state) return ""
        return state.resolution === "original" ? "Original" : "Low"
    })

    readonly actions: AssetMenuAction[] = [
        {id: "rerender", label: "Regenerate", icon: "fa-rotate-reverse"},
        {id: "remove", label: "Delete", icon: "fa-trash-can", variant: "destructive"},
    ]

    onAction(actionId: string) {
        const selectedType = this.$selectedType()
        if (actionId === "rerender") this.rerender.emit(selectedType)
        else if (actionId === "remove") this.remove.emit(selectedType)
    }

    onOptionMenuClick(option: OptionEntry) {
        this.$userSelectedType.set(option.type)
    }

    onOptionRenderClick(option: OptionEntry) {
        this.$userSelectedType.set(option.type)
        this.render.emit(option.type)
    }

    onOptionCancelClick(option: OptionEntry) {
        this.$userSelectedType.set(option.type)
        this.cancel.emit(option.type)
    }

    onRenderSelected() {
        this.render.emit(this.$selectedType())
    }
    onCancelSelected() {
        this.cancel.emit(this.$selectedType())
    }
    onRetrySelected() {
        this.retry.emit(this.$selectedType())
    }
    onRemoveSelected() {
        this.remove.emit(this.$selectedType())
    }
}

function isJobRunning(state: JobState): boolean {
    return state === JobState.Init || state === JobState.Runnable || state === JobState.Running
}
